import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
  signal,
  HostListener,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { LoadSearch } from '@app/loads/models/load-search.model';
import { AppService } from '@app/app.service';
import { TemplateDialogComponent } from '@app/shared/template-dialog/template-dialog.component';
import { STATES } from '@app/shared/constants/states';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { UserPlan, UserSettings } from '@app/auth/user.model';
import { AuthService } from '@app/auth/auth.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';

interface LocationOption {
  id?: number;
  city: string;
  state: string;
  zip?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

type LoadSearchFilters = LoadSearch['filters'];

@Component({
  selector: 'app-load-search-filters-dialog',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    FormsModule,
    ReactiveFormsModule,

    MatDialogModule,
    MatDialogTitle,
    MatDialogActions,
    MatDialogContent,

    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatTabsModule,
    MatDatepickerModule,
    MatChipsModule,
    MatDividerModule,
    MatExpansionModule,
  ],
  templateUrl: './load-search-filters-dialog.component.html',
  styleUrl: './load-search-filters-dialog.component.scss',
})
export class LoadSearchFiltersDialogComponent {

  testing: boolean = false;

  states = STATES;

  selectedDestinationTab = 0;
  skipDestinationTabClear = false;

  form: FormGroup;
  templateForm: FormGroup;

  loadSearch: LoadSearch | null = null;
  isPatching = false;

  templates: any[] = [];

  originOptions: LocationOption[] = [];
  destinationOptions: LocationOption[] = [];

  filteredOriginOptions: LocationOption[] = [];
  filteredDestinationOptions: LocationOption[] = [];

  loadingOrigin = signal(false);
  loadingDestination = signal(false);

  minSearchLength = 5;
  defaultSearchRadius = 250;
  minSearchRadius = 50;
  maxSearchRadius = 750;

  @ViewChild('originInput') originInput!: ElementRef<HTMLInputElement>;
  @ViewChild('originInput', { read: MatAutocompleteTrigger })
  originTrigger!: MatAutocompleteTrigger;

  @ViewChild('destinationInput') destinationInput!: ElementRef<HTMLInputElement>;
  @ViewChild('destinationInput', { read: MatAutocompleteTrigger })
  destinationTrigger!: MatAutocompleteTrigger;

  userPlan: UserPlan | null = null;
  userSettings: UserSettings | null = null;

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  today = this.startOfDay(new Date());

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<LoadSearchFiltersDialogComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      search?: LoadSearch | null;
      filters?: Partial<LoadSearchFilters> | null;
    }
  ) {
    this.loadSearch = data?.search ?? null;

    // User plan and settings
    this.userPlan = this.authService.getUserPlanFromStorage();
    this.userSettings = this.authService.getUserSettingsFromStorage();

    this.defaultSearchRadius =
      this.userSettings?.default_search_radius_miles ?? 250;

    this.maxSearchRadius =
      this.userPlan?.max_search_radius_miles ?? 750;

    this.form = this.fb.group({
      origin: [null, Validators.required],
      origin_latitude: [null, Validators.required],
      origin_longitude: [null, Validators.required],
      origin_radius: [
        null,
        [
          Validators.required,
          Validators.min(50),
          Validators.max(this.maxSearchRadius),
        ],
      ],

      origin_start_date: [null, [this.notPastDateValidator()]],
      origin_end_date: [null, [this.notPastDateValidator()]],

      destination: [null],
      destination_latitude: [null],
      destination_longitude: [null],
      destination_radius: [
        null,
        [
          Validators.min(this.minSearchRadius),
          Validators.max(this.maxSearchRadius),
        ],
      ],

      destination_states: [[]],
      destination_states_exclude: [[]],

      min_distance: [null, Validators.min(0)],
      max_distance: [null, Validators.min(0)],

      max_weight: [null, Validators.min(0)],

      max_length: [null, Validators.min(0)],
      max_width: [null, Validators.min(0)],
      max_height: [null, Validators.min(0)],

      max_well_length: [null, Validators.min(0)],

      min_rate: [null, Validators.min(0)],
      min_rpm: [null, Validators.min(0)],
    });

    this.templateForm = this.fb.group({
      id: [null as number | null],
    });

  }

  ngOnInit(): void {
    this.getTemplates();
    this.patchSearchFilters();
  }

  private notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      const selected = this.startOfDay(new Date(value));

      return selected < this.today ? { pastDate: true } : null;
    };
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  patchSearchFilters(): void {
    const filters: Partial<LoadSearchFilters> | null =
      this.loadSearch?.filters ?? this.data?.filters ?? null;

    if (!filters) {
      return;
    }

    this.isPatching = true;

    this.form.patchValue(
      {
        ...filters,
        origin_start_date: this.parseLocalDate(filters.origin_start_date),
        origin_end_date: this.parseLocalDate(filters.origin_end_date),
      },
      { emitEvent: false }
    );

    this.setDestinationTabFromFilters(filters);

    setTimeout(() => {
      this.isPatching = false;
    });
  }

  filter(controller: 'origin' | 'destination'): void {
    if (this.isPatching) {
      return;
    }

    const inputRef =
      controller === 'origin' ? this.originInput : this.destinationInput;

    const allOptions =
      controller === 'origin' ? this.originOptions : this.destinationOptions;

    const setFiltered =
      controller === 'origin'
        ? (opts: LocationOption[]) => (this.filteredOriginOptions = opts)
        : (opts: LocationOption[]) => (this.filteredDestinationOptions = opts);

    const value = inputRef.nativeElement.value.toLowerCase();

    if (value.length < this.minSearchLength) {
      setFiltered([]);
      return;
    }

    const alreadyInOptions = allOptions.some(
      (opt) => this.formatLocation(opt).toLowerCase() === value
    );

    if (!alreadyInOptions) {
      this.updateOptions(value, controller);
    }

    const matches = allOptions.filter((opt) =>
      this.formatLocation(opt).toLowerCase().includes(value)
    );

    setFiltered(matches);
  }

  updateOptions(search: string, controller: 'origin' | 'destination'): void {
    if (this.isPatching) {
      return;
    }

    const setLoading =
      controller === 'origin' ? this.loadingOrigin : this.loadingDestination;

    const setOptions =
      controller === 'origin'
        ? (opts: LocationOption[]) => {
          this.originOptions = opts;
          this.filteredOriginOptions = opts;
        }
        : (opts: LocationOption[]) => {
          this.destinationOptions = opts;
          this.filteredDestinationOptions = opts;
        };

    const trigger =
      controller === 'origin' ? this.originTrigger : this.destinationTrigger;

    setLoading.set(true);

    this.appService.getLocations(search).subscribe({
      next: (res) => {
        const data = Array.isArray(res.data) ? res.data : [];

        setOptions(data);
        setLoading.set(false);

        setTimeout(() => trigger.openPanel());
      },
      error: (err) => {
        console.error('Error:', err);
        setOptions([]);
        setLoading.set(false);
      },
    });
  }

  formatLocation(item: LocationOption | null): string {
    if (!item) return '';

    return `${item.city}, ${item.state}${item.zip ? ` ${item.zip}` : ''}`;
  }

  onLocationSelected(
    item: LocationOption,
    controller: 'origin' | 'destination'
  ): void {
    if (!item) return;

    if (controller === 'origin') {
      this.form.patchValue({
        origin: this.formatLocation(item),
        origin_latitude: item.latitude ?? null,
        origin_longitude: item.longitude ?? null,
        origin_radius: this.defaultSearchRadius,
      });
    }

    if (controller === 'destination') {
      this.form.patchValue({
        destination: this.formatLocation(item),
        destination_latitude: item.latitude ?? null,
        destination_longitude: item.longitude ?? null,
        destination_radius: this.defaultSearchRadius,
      });
    }
  }

  saveSearch(): void {

    if (this.form.invalid) {

      if(this.testing) {
        this.logInvalidFields(this.form);
      }

      this.appService.showSnackBar('Please fill in all required fields', 'error');
    
      this.form.markAllAsTouched();

      return;
    }

    if (this.loadSearch?.id) {
      this.updateSearch();
      return;
    }

    this.createSearch();
  }

  createSearch(): void {
    this.appService.post('loads/searches', this.getCleanFormValues()).subscribe({
      next: (res) => {
        this.dialogRef.close(res.data);
      },
      error: (err) => {
        console.error('Error:', err);
        this.appService.showSnackBar(err.error.message, 'error');
      },
    });
  }

  updateSearch(): void {
    if (!this.loadSearch?.id) {
      return;
    }

    this.appService
      .patch(`loads/searches/${this.loadSearch.id}`, this.getCleanFormValues())
      .subscribe({
        next: (res) => {
          this.dialogRef.close(res.data);
        },
        error: (err) => {
          console.error('Error:', err);
          this.appService.showSnackBar(err.error.message, 'error');
        },
      });
  }

  clearFilters(): void {
    this.form.reset();
  }

  close(): void {
    this.dialogRef.close();
  }

  getTemplates(): void {
    this.appService.getMyLoadSearchTemplates().subscribe({
      next: (res) => {
        this.templates = res.data;
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  addTemplate(mode: 'copy' | 'create' = 'create'): void {
    if (mode === 'copy' && !this.templateForm.get('id')?.value) {
      return;
    }

    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode,
        name: selectedTemplate?.name ?? null,
        type: 'load search',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        filters: this.getCleanFormValues(),
      };

      this.appService.post('loads/search-templates', payload).subscribe({
        next: (response) => {

          this.getTemplates();
          this.templateForm.get('id')?.setValue(response.data.id);
        },
        error: (err) => this.appService.handleError(err),
      });
    });
  }

  updateTemplate(): void {
    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    if (!selectedTemplate) return;

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode: 'update',
        name: selectedTemplate.name,
        type: 'load search',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        filters: this.getCleanFormValues(),
      };

      this.appService
        .patch(`loads/search-templates/${selectedTemplate.id}`, payload)
        .subscribe({
          next: (response) => {
            this.getTemplates();
          },
          error: (err) => this.appService.handleError(err),
        });
    });
  }

  deleteTemplate(): void {
    const id = this.templateForm.get('id')?.value;

    if (!id) return;

    this.appService
      .confirm('Are you sure you want to delete this template?')
      .subscribe((ok) => {
        if (!ok) return;

        this.appService.delete(`loads/search-templates/${id}`).subscribe({
          next: (response) => {
            this.templateForm.reset();
            this.getTemplates();
          },
          error: (err) => this.appService.handleError(err),
        });
      });
  }

  onTemplateChange(): void {
    const id = this.templateForm.get('id')?.value;

    if (!id) {
      this.form.reset();
      this.selectedDestinationTab = 0;
      return;
    }

    const selected = this.templates.find((t: any) => t.id === id);

    if (!selected) {
      this.form.reset();
      this.selectedDestinationTab = 0;
      return;
    }

    const filters = selected.filters ?? {};

    this.form.reset();
    this.form.patchValue({
      ...filters,
      destination_states: filters.destination_states ?? [],
      destination_states_exclude: filters.destination_states_exclude ?? [],
    });

    this.setDestinationTabFromFilters(filters);
  }

  private setDestinationTabFromFilters(filters: any): void {
    // Temqorarily skip clearing other fields when setting the destination tab,
    // to avoid clearing values that are being patched into the form
    this.skipDestinationTabClear = true;

    if (filters.destination) {
      this.selectedDestinationTab = 0;
    } else if (filters.destination_states?.length) {
      this.selectedDestinationTab = 1;
    } else if (filters.destination_states_exclude?.length) {
      this.selectedDestinationTab = 2;
    } else {
      this.selectedDestinationTab = 0;
    }

    setTimeout(() => {
      this.skipDestinationTabClear = false;
    },1);
  }

  private getCleanFormValues(): any {
    const raw = this.form.getRawValue();
    const clean: any = {};

    Object.keys(raw).forEach((key) => {
      let value = raw[key];

      if (key === 'origin_start_date' || key === 'origin_end_date') {
        value = this.formatDate(value);
      }

      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        clean[key] = value;
      }
    });

    return clean;
  }

  clearDestination(): void {
    this.form.get('destination')?.setValue(null);
    this.form.get('destination_latitude')?.setValue(null);
    this.form.get('destination_longitude')?.setValue(null);
    this.form.get('destination_radius')?.setValue(null);
  }

  clearDestinationStates(): void {
    this.form.get('destination_states')?.setValue([]);
  }

  clearDestinationStatesExclude(): void {
    this.form.get('destination_states_exclude')?.setValue([]);
  }

  onTabChange(): void {
    console.log("this.skipDestinationTabClear:", this.skipDestinationTabClear);
    if (this.skipDestinationTabClear) {
      return;
    }

    this.clearDestination();
    this.clearDestinationStates();
    this.clearDestinationStatesExclude();
  }

  private logInvalidFields(form: FormGroup): void {
    const invalidFields: Record<string, any> = {};

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control?.invalid) {
        invalidFields[key] = {
          value: control.value,
          errors: control.errors,
        };
      }
    });

    console.table(invalidFields);
  }

  private formatDate(value: any): string | null {
    if (!value) return null;

    if (typeof value === 'string') return value;

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private parseLocalDate(date: string | Date | null | undefined): Date | null {
    if (!date) return null;

    if (date instanceof Date) {
      return date;
    }

    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month - 1, day);
  }

  removeDestinationState(stateCode: string): void {
    const current = this.form.get('destination_states')?.value ?? [];

    this.form.get('destination_states')?.setValue(
      current.filter((s: string) => s !== stateCode)
    );
  }

  removeDestinationExcludedState(stateCode: string): void {
    const current = this.form.get('destination_states_exclude')?.value ?? [];

    this.form.get('destination_states_exclude')?.setValue(
      current.filter((s: string) => s !== stateCode)
    );
  }

  clearDateRange(): void {
    this.form.get('origin_start_date')?.setValue(null);
    this.form.get('origin_end_date')?.setValue(null);
  }
  
}